<script setup lang="ts">
import * as v from 'valibot'
import type { Form, FormSubmitEvent } from '#ui/types'

const props = withDefaults(
  defineProps<{
    hideEmailField?: boolean
    emailFromHash?: string | null
    fromHashLink?: boolean
    /** Mientras `useFetch` a `/user-by-hash` está pendiente (p. ej. navegación cliente). */
    userByHashPending?: boolean
  }>(),
  {
    hideEmailField: false,
    emailFromHash: null,
    fromHashLink: false,
    userByHashPending: false
  }
)

const isMounted = ref(false)

onMounted(() => {
  isMounted.value = true
})

const showEmailField = computed(() => {
  if (!props.fromHashLink) {
    return !props.hideEmailField
  }
  if (!isMounted.value || props.userByHashPending) {
    return false
  }
  return !props.hideEmailField
})

const { locale, t } = useI18n()
const localePath = useLocalePath()

const schema = v.object({
  reference: v.pipe(v.string(), v.trim(), v.minLength(1, t('labels.cancelBook.referenceError'))),
  email: v.pipe(v.string(), v.trim(), v.email(t('errors.login.email')))
})

type Schema = v.InferOutput<typeof schema>

const state = reactive({
  reference: '',
  email: ''
})

watch(() => props.emailFromHash, (email) => {
  if (email) {
    state.email = email
  }
}, { immediate: true })

const form = ref<Form<Schema>>()
const serverError = ref('')
const successMessage = ref('')
const pending = ref(false)

async function onSubmit(event: FormSubmitEvent<Schema>) {
  form.value?.clear()
  serverError.value = ''
  successMessage.value = ''
  pending.value = true

  const email = (
    props.hideEmailField && props.emailFromHash
      ? props.emailFromHash
      : event.data.email
  )?.trim() ?? ''

  try {
    await $fetch(`${useBaseUrl()}/cancel-reservation-by-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: {
        reference: event.data.reference.trim(),
        email,
        locale: locale.value
      }
    })
    await refreshNuxtData(['active-bookings', 'past-bookings'])
    successMessage.value = t('labels.cancelBook.success')
  } catch (err: unknown) {
    const e = err as { data?: { msg?: string }; message?: string }
    serverError.value = e?.data?.msg || e?.message || t('labels.cancelBook.genericError')
  } finally {
    pending.value = false
  }
}
</script>

<template>
  <div class="w-full p-6 sm:p-10">
    <div class="max-w-md mx-auto flex flex-col gap-8">
      <div class="space-y-3 text-center">
        <h1 class="text-4xl">
          {{ t('labels.cancelBook.title') }}
        </h1>
        <p class="text-sm text-muted">
          {{ t('labels.cancelBook.subtitle') }}
        </p>
      </div>

      <UForm
        v-if="!successMessage"
        ref="form"
        :schema="schema"
        class="flex flex-col gap-4"
        :state="state"
        @submit="onSubmit"
      >
        <UFormField name="reference" required>
          <FloatingLabelInput
            v-model="state.reference"
            :label="t('labels.user_zone.reservas.reference')"
            required
            size="lg"
            autocomplete="off"
            :disabled="pending"
          />
        </UFormField>

        <UFormField v-if="showEmailField" name="email" required>
          <FloatingLabelInput
            v-model="state.email"
            type="email"
            :label="t('labels.login.placeholder.email')"
            size="lg"
            autocomplete="email"
            :disabled="pending"
          />
        </UFormField>

        <UAlert
          v-if="serverError"
          icon="i-lucide-alert-circle"
          :title="serverError"
          color="error"
          variant="soft"
        />

        <UButton
          type="submit"
          class="justify-center bg-byhours-gradient"
          variant="solid"
          size="xl"
          :loading="pending"
        >
          {{ t('labels.cancelBook.submit') }}
        </UButton>
      </UForm>

      <template v-else>
        <UAlert
          icon="i-lucide-check-circle-2"
          :title="successMessage"
          color="success"
          variant="soft"
        />
        <UButton
          :to="localePath('/user-zone/bookings')"
          class="justify-center bg-byhours-gradient"
          size="xl"
        >
          {{ t('confirm.buttons.viewBookings') }}
        </UButton>
      </template>

      <UButton
        v-if="!successMessage"
        :to="localePath('/')"
        variant="link"
        size="xl"
        class="justify-center"
      >
        {{ t('labels.cancelBook.backHome') }}
      </UButton>

      <USeparator />

      <div class="flex flex-col gap-2">
        <p class="font-bold">{{ t('checkout.paymentDetails.support.title') }}</p>
        <div class="text-sm">
          <p class="mt-1">{{ t('checkout.paymentDetails.support.scheduleMondayThursday') }}</p>
          <p>{{ t('checkout.paymentDetails.support.scheduleFriday') }}</p>
          <p>{{ t('checkout.paymentDetails.support.scheduleWeekend') }}</p>
        </div>
        <p class="text-sm mt-1">
          {{ t('checkout.paymentDetails.support.whatsapp') }}
          <a
            href="https://wa.me/34629380206"
            target="_blank"
            rel="noopener noreferrer"
            class="text-orange-500 hover:text-orange-600"
          >Whatsapp</a>
        </p>
        <p class="text-sm mt-1">
          {{ t('checkout.paymentDetails.support.email') }}
          <a href="mailto:hello@byhours.com" class="text-orange-500 hover:text-orange-600">hello@byhours.com</a>
        </p>
        <ul class="text-sm gap-2 flex flex-col mt-2">
          <li>🇪🇸 {{ t('checkout.paymentDetails.support.spain') }} <span class="text-neutral-500">+34 602 225 694</span></li>
          <li>🇨🇴 {{ t('checkout.paymentDetails.support.colombia') }} <span class="text-neutral-500">+57 1 4898 378</span></li>
          <li>🇲🇽 {{ t('checkout.paymentDetails.support.mexico') }} <span class="text-neutral-500">+52 55 8526 4283</span></li>
          <li>🇫🇷 {{ t('checkout.paymentDetails.support.france') }} <span class="text-neutral-500">+33 1 7890 2675</span></li>
          <li>🇬🇧 {{ t('checkout.paymentDetails.support.uk') }} <span class="text-neutral-500">+44 20 3769 0610</span></li>
        </ul>
      </div>
    </div>
  </div>
</template>
